import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Radio as RadioIcon, Plus, Pencil, Trash2, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { RadioModuleDialog } from "@/components/RadioModuleDialog";

const EMPTY_RADIO = { matricola: "", tipo: "Portatile", modello: "", selettiva: "" };

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export default function RadioPage() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const [radios, setRadios] = useState([]);
  const [radioForm, setRadioForm] = useState(EMPTY_RADIO);
  const [editingRadio, setEditingRadio] = useState(null);
  const [savingRadio, setSavingRadio] = useState(false);

  const [modules, setModules] = useState([]);
  const [services, setServices] = useState([]);
  const [modForm, setModForm] = useState({ source: "libero", service_id: "", event_name: "", event_date: "" });
  const [openModule, setOpenModule] = useState(null);

  const isCoord = user.role === "coordinatore";

  useEffect(() => {
    if (!isCoord) { navigate("/"); return; }
    api.get("/radios").then(({ data }) => setRadios(data)).catch(() => {});
    api.get("/radio-modules").then(({ data }) => setModules(data)).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data.filter((s) => !s.archived))).catch(() => {});
  }, [isCoord, navigate]);

  const loadRadios = () => api.get("/radios").then(({ data }) => setRadios(data)).catch(() => {});
  const loadModules = () => api.get("/radio-modules").then(({ data }) => setModules(data)).catch(() => {});
  const refresh = () => { loadModules(); loadRadios(); };

  const saveRadio = async (e) => {
    e.preventDefault();
    if (!radioForm.matricola.trim()) { toast.error("Inserisci la matricola"); return; }
    setSavingRadio(true);
    try {
      if (editingRadio) {
        const { data } = await api.put(`/radios/${editingRadio}`, radioForm);
        setRadios((p) => p.map((x) => (x.id === editingRadio ? data : x)).sort((a, b) => a.matricola.localeCompare(b.matricola)));
        toast.success("Radio aggiornata");
      } else {
        const { data } = await api.post("/radios", radioForm);
        setRadios((p) => [...p, data].sort((a, b) => a.matricola.localeCompare(b.matricola)));
        toast.success("Radio aggiunta al parco");
      }
      setRadioForm(EMPTY_RADIO);
      setEditingRadio(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingRadio(false);
    }
  };

  const editRadio = (r) => { setEditingRadio(r.id); setRadioForm({ matricola: r.matricola, tipo: r.tipo || "Portatile", modello: r.modello || "", selettiva: r.selettiva || "" }); };
  const deleteRadio = async (r) => {
    try {
      await api.delete(`/radios/${r.id}`);
      setRadios((p) => p.filter((x) => x.id !== r.id));
      if (editingRadio === r.id) { setEditingRadio(null); setRadioForm(EMPTY_RADIO); }
      toast.success("Radio eliminata");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const createModule = async (e) => {
    e.preventDefault();
    const payload = modForm.source === "servizio"
      ? { service_id: modForm.service_id }
      : { event_name: modForm.event_name, event_date: modForm.event_date };
    if (modForm.source === "servizio" && !modForm.service_id) { toast.error("Scegli un servizio"); return; }
    if (modForm.source === "libero" && !modForm.event_name.trim()) { toast.error("Inserisci il nome evento"); return; }
    try {
      const { data } = await api.post("/radio-modules", payload);
      setModules((p) => [data, ...p]);
      setModForm({ source: "libero", service_id: "", event_name: "", event_date: "" });
      toast.success("Modulo creato");
      const { data: full } = await api.get(`/radio-modules/${data.id}`);
      setOpenModule(full);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const openMod = async (m) => {
    try { const { data } = await api.get(`/radio-modules/${m.id}`); setOpenModule(data); }
    catch (err) { toast.error("Impossibile aprire il modulo"); }
  };

  const deleteModule = async (m) => {
    try { await api.delete(`/radio-modules/${m.id}`); setModules((p) => p.filter((x) => x.id !== m.id)); toast.success("Modulo eliminato"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" data-testid="radio-back" onClick={() => navigate("/")} className="rounded-full"><ArrowLeft className="h-5 w-5" /></Button>
          <BrandLogo logoUrl={settings.logo_url} size={38} />
          <div className="leading-tight">
            <p className="font-display font-bold text-sm sm:text-base flex items-center gap-1.5"><RadioIcon className="h-4 w-4" /> Gestione Radio</p>
            <p className="text-xs text-muted-foreground hidden sm:block">{settings.committee_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="moduli">
          <TabsList>
            <TabsTrigger value="moduli" data-testid="tab-moduli">Moduli di consegna</TabsTrigger>
            <TabsTrigger value="parco" data-testid="tab-parco-radio">Parco Radio</TabsTrigger>
          </TabsList>

          {/* MODULI */}
          <TabsContent value="moduli" className="mt-6 space-y-6">
            <form onSubmit={createModule} className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="create-module-form">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Plus className="h-5 w-5" /> Nuovo modulo di consegna</h3>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={modForm.source === "libero" ? "default" : "outline"} onClick={() => setModForm((p) => ({ ...p, source: "libero" }))} className="rounded-full" data-testid="src-libero">Evento libero</Button>
                <Button type="button" size="sm" variant={modForm.source === "servizio" ? "default" : "outline"} onClick={() => setModForm((p) => ({ ...p, source: "servizio" }))} className="rounded-full" data-testid="src-servizio">Da servizio</Button>
              </div>
              {modForm.source === "servizio" ? (
                <div className="space-y-1">
                  <Label>Servizio</Label>
                  <Select value={modForm.service_id} onValueChange={(v) => setModForm((p) => ({ ...p, service_id: v }))}>
                    <SelectTrigger data-testid="module-service-select"><SelectValue placeholder="Scegli un servizio…" /></SelectTrigger>
                    <SelectContent>
                      {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.event_name} · {fmt(s.date_time)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Nome evento</Label>
                    <Input data-testid="module-event-name" value={modForm.event_name} onChange={(e) => setModForm((p) => ({ ...p, event_name: e.target.value }))} placeholder="Festa del paese" />
                  </div>
                  <div className="space-y-1">
                    <Label>Data</Label>
                    <Input type="datetime-local" data-testid="module-event-date" value={modForm.event_date} onChange={(e) => setModForm((p) => ({ ...p, event_date: e.target.value }))} />
                  </div>
                </div>
              )}
              <Button type="submit" data-testid="create-module-button" className="rounded-full font-semibold">Crea modulo</Button>
            </form>

            <div className="grid sm:grid-cols-2 gap-4" data-testid="modules-list">
              {modules.map((m) => (
                <div key={m.id} data-testid={`module-card-${m.id}`} className="rounded-xl border border-border bg-card p-5 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-display font-semibold truncate">{m.event_name}</p>
                      <p className="text-xs text-muted-foreground">{fmt(m.event_date)}</p>
                    </div>
                    <RadioIcon className="h-5 w-5 text-[#D32F2F] shrink-0" />
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{m.assigned_count} assegnate</Badge>
                    <Badge variant="outline">{m.returned_count} riconsegnate</Badge>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" onClick={() => openMod(m)} data-testid={`open-module-${m.id}`} className="rounded-full"><FolderOpen className="h-4 w-4 mr-1" /> Apri</Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`delete-module-${m.id}`} className="rounded-full text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare il modulo "{m.event_name}"?</AlertDialogTitle>
                          <AlertDialogDescription>Tutte le assegnazioni e le firme verranno eliminate.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteModule(m)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
              {modules.length === 0 && <p className="text-sm text-muted-foreground">Nessun modulo. Creane uno per iniziare la consegna radio.</p>}
            </div>
          </TabsContent>

          {/* PARCO RADIO */}
          <TabsContent value="parco" className="mt-6 grid lg:grid-cols-2 gap-6">
            <form onSubmit={saveRadio} className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="radio-form">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><RadioIcon className="h-5 w-5" /> {editingRadio ? "Modifica radio" : "Nuova radio"}</h3>
              <div className="space-y-2">
                <Label>Matricola</Label>
                <Input data-testid="radio-matricola-input" required value={radioForm.matricola} onChange={(e) => setRadioForm((p) => ({ ...p, matricola: e.target.value }))} placeholder="R-001" />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={radioForm.tipo} onValueChange={(v) => setRadioForm((p) => ({ ...p, tipo: v }))}>
                  <SelectTrigger data-testid="radio-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Portatile">Portatile</SelectItem>
                    <SelectItem value="Fissa">Fissa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Modello</Label>
                <Input data-testid="radio-modello-input" value={radioForm.modello} onChange={(e) => setRadioForm((p) => ({ ...p, modello: e.target.value }))} placeholder="Motorola GP340" />
              </div>
              <div className="space-y-2">
                <Label>Selettiva</Label>
                <Input data-testid="radio-selettiva-input" value={radioForm.selettiva} onChange={(e) => setRadioForm((p) => ({ ...p, selettiva: e.target.value }))} placeholder="101" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="save-radio-button" disabled={savingRadio} className="rounded-full font-semibold">
                  {savingRadio ? "Salvataggio…" : editingRadio ? "Salva modifiche" : "Aggiungi radio"}
                </Button>
                {editingRadio && <Button type="button" variant="ghost" onClick={() => { setEditingRadio(null); setRadioForm(EMPTY_RADIO); }} className="rounded-full">Annulla</Button>}
              </div>
            </form>

            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-semibold mb-4">Parco Radio ({radios.length})</h3>
              <div className="space-y-2 max-h-[520px] overflow-y-auto" data-testid="radios-list">
                {radios.map((r) => (
                  <div key={r.id} data-testid={`radio-row-${r.id}`} className="rounded-lg border border-border p-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm flex items-center gap-2">{r.matricola}
                        {r.in_use && <Badge className="bg-amber-500 text-white text-[10px]">In uso</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground">{[r.tipo, r.modello, r.selettiva && `Sel. ${r.selettiva}`].filter(Boolean).join(" · ")}</p>
                      {r.in_use && <p className="text-[11px] text-amber-700">{r.in_use_by} · {r.in_use_event}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" data-testid={`edit-radio-${r.id}`} onClick={() => editRadio(r)} className="h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`delete-radio-${r.id}`} className="h-8 w-8 p-0 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare la radio {r.matricola}?</AlertDialogTitle>
                            <AlertDialogDescription>I moduli già creati non saranno modificati.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteRadio(r)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
                {radios.length === 0 && <p className="text-sm text-muted-foreground">Nessuna radio nel parco.</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <RadioModuleDialog open={!!openModule} onOpenChange={(o) => !o && setOpenModule(null)} module={openModule} radios={radios} onChanged={refresh} />
    </div>
  );
}
